import { useMutation, useQuery } from '@tanstack/react-query'
import { EventEntityType, EventType, type OfferChannel, OfferStatus, Permission } from '@today/types'
import { OfferDetailState, type OfferDetailViewProps, OfferSendAction } from '@today/ui-domain'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { OFFERS_NAMESPACE } from '@/i18n'
import { subscribeRealtime } from '@/realtime/realtime-dispatcher'
import { offerKeys, offerService } from '@/services/offer.service'
import { apiMessage } from './api-message'
import { deriveGeneration, matchesRenderFailure, POLL_INTERVAL_MS, POLL_WINDOW_MS } from './offer-generation'

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status

/** A 403/404 answer is final for this id: no retry, no polling. */
const isFinal = (status: number | undefined) => status === 403 || status === 404

/**
 * A final 403/404 outranks cached data: an offer that became unreadable mid-poll
 * (deleted, moved, permission revoked) must stop showing its last tiles and send
 * action. A 5xx keeps the last data (N3).
 */
const detailState = (hasData: boolean, isPending: boolean, status: number | undefined): OfferDetailState => {
	if (status === 404) return OfferDetailState.NOT_FOUND
	if (hasData) return OfferDetailState.LOADED
	if (isPending) return OfferDetailState.LOADING
	return OfferDetailState.ERROR
}

/** The polling window (BR-7/BR-8): when it started, whether it expired, and a restart. */
function usePollWindow() {
	const [windowStart, setWindowStart] = useState(() => Date.now())
	const [timedOut, setTimedOut] = useState(false)
	useEffect(() => {
		const timer = setTimeout(() => setTimedOut(true), POLL_WINDOW_MS - (Date.now() - windowStart))
		return () => clearTimeout(timer)
	}, [windowStart])
	const restart = () => {
		setTimedOut(false)
		setWindowStart(Date.now())
	}
	return { windowStart, timedOut, restart }
}

type Artefact = 'landing' | 'pdf'
const FAILURE_EVENT: Record<Artefact, EventType> = {
	landing: EventType.OFFER_LANDING_FAILED,
	pdf: EventType.OFFER_PDF_FAILED,
}

/**
 * BR-9: per artefact, true once a realtime failure hint for this offer's current
 * render arrives. The hint is recorded against the render it named, so a newer
 * render (Re-send) clears it.
 */
function useFailureHints(offerId: string, currentRenderId: string | undefined): Record<Artefact, boolean> {
	const [failedRender, setFailedRender] = useState<Partial<Record<Artefact, string>>>({})
	const renderRef = useRef(currentRenderId)
	renderRef.current = currentRenderId
	useEffect(() => {
		setFailedRender({})
		const unsubscribe = (Object.keys(FAILURE_EVENT) as Artefact[]).map((artefact) =>
			subscribeRealtime(
				{ type: FAILURE_EVENT[artefact], entityType: EventEntityType.OFFER, entityId: offerId },
				(message) => {
					const renderId = renderRef.current
					if (renderId && matchesRenderFailure(message, offerId, renderId)) {
						setFailedRender((failed) => ({ ...failed, [artefact]: renderId }))
					}
				},
			),
		)
		return () => {
			for (const off of unsubscribe) off()
		}
	}, [offerId])
	const failed = (artefact: Artefact) => currentRenderId !== undefined && failedRender[artefact] === currentRenderId
	return { landing: failed('landing'), pdf: failed('pdf') }
}

/**
 * One poll per answer (BR-7): the next refetch is due 5 s after the last answer,
 * and is scheduled only while polling applies, never after a final 403/404, and
 * only inside the window.
 */
function usePolling(poll: {
	shouldPoll: boolean
	errorStatus: number | undefined
	answeredAt: number
	windowStart: number
	refetch: () => unknown
}): void {
	const { shouldPoll, errorStatus, answeredAt, windowStart, refetch } = poll
	useEffect(() => {
		if (!shouldPoll || isFinal(errorStatus)) return
		if (answeredAt + POLL_INTERVAL_MS >= windowStart + POLL_WINDOW_MS) return
		const timer = setTimeout(() => void refetch(), POLL_INTERVAL_MS)
		return () => clearTimeout(timer)
	}, [shouldPoll, errorStatus, windowStart, refetch, answeredAt])
}

/** The offer read: a 403/404 is final (no retry); a realtime hint, not focus, drives refetches. */
function useOfferQuery(offerId: string) {
	return useQuery({
		queryKey: offerKeys.detail(offerId),
		queryFn: () => offerService.get(offerId),
		retry: false,
		refetchOnWindowFocus: false,
	})
}

/** Re-send (BR-13): `POST /offers/{id}/send` with the failed attempt's channels; `onAccepted` on `202`. */
function useResend(offerId: string, channels: OfferChannel[], onAccepted: () => void) {
	return useMutation({ mutationFn: () => offerService.send(offerId, channels), onSuccess: onAccepted })
}

/**
 * Discard (TO-125 REQ-108): SEND_FAILED → ERROR behind a confirmation, then a
 * refetch. Offered only on a SEND_FAILED offer to an `offer.write` holder.
 */
function useDiscard(offerId: string, offered: boolean, refetch: () => unknown, t: (key: string) => string) {
	const [open, setOpen] = useState(false)
	const mutation = useMutation({
		mutationFn: () => offerService.transition(offerId, OfferStatus.ERROR),
		onSuccess: () => {
			setOpen(false)
			void refetch()
		},
	})
	if (!offered) return null
	return {
		open,
		pending: mutation.isPending,
		error: mutation.error ? (apiMessage(mutation.error) ?? t('detail.discardError')) : null,
		onOpen: () => setOpen(true),
		onConfirm: () => {
			if (!mutation.isPending) mutation.mutate()
		},
		onCancel: () => setOpen(false),
	}
}

/**
 * The offer detail (TO-466 offer-generation-status REQ-007..REQ-011,
 * offer-creation-flow §7): loads the offer, refetches on the realtime hint (the
 * query bridge invalidates `['offers']`), polls every {@link POLL_INTERVAL_MS}
 * while a render is in flight inside a {@link POLL_WINDOW_MS} window, and
 * derives the tiles, version line and send action. Re-send posts the failed
 * attempt's channels.
 */
export function useOfferDetail(offerId: string): Omit<OfferDetailViewProps, 't'> & { forbidden: boolean } {
	const { t, i18n } = useTranslation(OFFERS_NAMESPACE)
	const { permissions } = useViewerPermissions()
	const navigate = useNavigate()
	const query = useOfferQuery(offerId)
	const { windowStart, timedOut, restart } = usePollWindow()
	const failed = useFailureHints(offerId, query.data?.currentRenderId)
	const canWrite = permissions.includes(Permission.OFFER_WRITE)
	const view = deriveGeneration({ offer: query.data, failed, timedOut, canSend: canWrite })
	const errorStatus = statusOf(query.error)
	const { refetch } = query
	const restartWindow = () => {
		restart()
		void refetch()
	}
	const resend = useResend(offerId, query.data?.sendChannels ?? [], restartWindow)
	usePolling({
		shouldPoll: view.shouldPoll,
		errorStatus,
		answeredAt: Math.max(query.dataUpdatedAt, query.errorUpdatedAt),
		windowStart,
		refetch,
	})

	const offer = query.data
	const discard = useDiscard(offerId, canWrite && offer?.status === OfferStatus.SEND_FAILED, refetch, t)
	return {
		forbidden: errorStatus === 403,
		state: detailState(offer !== undefined, query.isPending, errorStatus),
		status: offer?.status ?? null,
		createdAt: offer ? new Date(offer.createdAt).toLocaleString(i18n.language) : null,
		version: view.version,
		tiles: view.tiles,
		showCheckAgain: view.showCheckAgain,
		renderFailure: offer?.renderFailure ?? null,
		sendAction: view.sendAction,
		isSending: resend.isPending,
		sendError: resend.error ? (apiMessage(resend.error) ?? t('detail.sendError')) : null,
		onCheckAgain: restartWindow,
		onRetry: () => void refetch(),
		onSend: () => {
			// A DRAFT's first send is wizard Step 4 (TO-125); Re-send reuses the failed attempt's channels (BR-13).
			if (view.sendAction === OfferSendAction.RESEND) resend.mutate()
			else if (view.sendAction === OfferSendAction.SEND) void navigate(`/offers/${encodeURIComponent(offerId)}/send`)
		},
		discard,
	}
}
